import React, { useState, useRef, useEffect } from 'react';
import '../styles/ChatWindow.css';
import FileUpload from './FileUpload.tsx';
import CodeBlock, { extractCodeBlocks, replaceCodeBlocks } from './CodeBlock.tsx';

interface Message {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  attachedFile?: { name: string; type: string };
  createdAt: string;
  tokensIn?: number;
  tokensOut?: number;
  durationMs?: number;
}

interface Props {
  messages: Message[];
  mode: string;
  onSendMessage: (content: string, file?: File) => void;
  loading: boolean;
  streamingTokensIn: number;
  streamingTokensOut: number;
  streamingElapsed: number;
  workspace: string | null;
  isCodingMode?: boolean;
}

const MODE_ICONS: Record<string, string> = {
  GOVERNANCE: '🎯', CAPITAL: '💰', LEGAL: '⚖️', TECHNOLOGY: '⚙️',
  BRAND: '🎨', OPERATIONS: '📋', MARKETING: '📢',
  GROWTH: '📈', PARTNERSHIPS: '🤝', CUSTOMER: '❤️',
  AUDIT: '🔍', REALWORLD: '🌍', TIMING: '⏱️', AIINFRA: '🧠',
};

const MODE_NAMES: Record<string, string> = {
  GOVERNANCE: 'VELA', CAPITAL: 'MONETA', LEGAL: 'FIDIUS', TECHNOLOGY: 'VULCAN',
  BRAND: 'VENUS', OPERATIONS: 'OPERA', MARKETING: 'MERCURY',
  GROWTH: 'FAUNUS', PARTNERSHIPS: 'JANUS', CUSTOMER: 'VESTA',
  AUDIT: 'CUSTOS', REALWORLD: 'CURA', TIMING: 'KAIROS', AIINFRA: 'NESTOR',
};

export default function ChatWindowV2({
  messages, mode, onSendMessage, loading,
  streamingTokensIn, streamingTokensOut, streamingElapsed,
  workspace, isCodingMode
}: Props) {
  const [input, setInput] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const autoResize = (el: HTMLTextAreaElement) => {
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight, 200) + 'px';
  };

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInput(e.target.value);
    autoResize(e.target);
  };

  const handleSend = () => {
    if (input.trim() && !loading) {
      onSendMessage(input, selectedFile || undefined);
      setInput('');
      setSelectedFile(null);
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto';
      }
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }
  };

  // Inline markdown
  const inlineMarkdown = (text: string, pfx: string): React.ReactNode[] => {
    const nodes: React.ReactNode[] = [];
    const re = /\*\*(.+?)\*\*|`(.+?)`|\*([^*\n]+?)\*|\[([^\]]+)\]\(([^)]+)\)/g;
    let last = 0, k = 0, m: RegExpExecArray | null;
    while ((m = re.exec(text)) !== null) {
      if (m.index > last) nodes.push(<React.Fragment key={`${pfx}t${k++}`}>{text.slice(last, m.index)}</React.Fragment>);
      if (m[1] != null) nodes.push(<strong key={`${pfx}b${k++}`}>{m[1]}</strong>);
      else if (m[2] != null) nodes.push(<code key={`${pfx}c${k++}`} className="inline-code">{m[2]}</code>);
      else if (m[3] != null) nodes.push(<em key={`${pfx}i${k++}`}>{m[3]}</em>);
      else if (m[4] != null) nodes.push(<a key={`${pfx}l${k++}`} href={m[5]} target="_blank" rel="noopener noreferrer">{m[4]}</a>);
      last = m.index + m[0].length;
    }
    if (last < text.length) nodes.push(<React.Fragment key={`${pfx}t${k}`}>{text.slice(last)}</React.Fragment>);
    return nodes;
  };

  const renderLines = (text: string, pi: number): React.ReactNode => {
    const lines = text.split('\n');
    const out: React.ReactNode[] = [];
    let i = 0;
    while (i < lines.length) {
      const line = lines[i];
      const k = `${pi}-${i}`;
      if (/^-{3,}$|^\*{3,}$|^_{3,}$/.test(line.trim())) { out.push(<hr key={k} />); i++; continue; }
      if (line.startsWith('### ')) { out.push(<h3 key={k}>{inlineMarkdown(line.slice(4), k)}</h3>); i++; continue; }
      if (line.startsWith('## '))  { out.push(<h2 key={k}>{inlineMarkdown(line.slice(3), k)}</h2>); i++; continue; }
      if (line.startsWith('# '))   { out.push(<h1 key={k}>{inlineMarkdown(line.slice(2), k)}</h1>); i++; continue; }
      if (line.startsWith('> '))   { out.push(<blockquote key={k}>{inlineMarkdown(line.slice(2), k)}</blockquote>); i++; continue; }
      if (/^[-*] |^\d+\. /.test(line)) {
        const isOl = /^\d+\. /.test(line);
        const items: React.ReactNode[] = [];
        while (i < lines.length && /^[-*] |^\d+\. /.test(lines[i])) {
          const lk = `${pi}-${i}`;
          const txt = lines[i].replace(/^[-*] /, '').replace(/^\d+\. /, '');
          items.push(<li key={lk}>{inlineMarkdown(txt, lk)}</li>);
          i++;
        }
        out.push(isOl ? <ol key={`${pi}-ol${i}`}>{items}</ol> : <ul key={`${pi}-ul${i}`}>{items}</ul>);
        continue;
      }
      if (line.trim() === '') { out.push(<br key={k} />); i++; continue; }
      out.push(<p key={k}>{inlineMarkdown(line, k)}</p>);
      i++;
    }
    return <>{out}</>;
  };

  const renderContent = (content: string) => {
    const codeBlocks = extractCodeBlocks(content);
    const textParts = replaceCodeBlocks(content).split('[CODE_BLOCK]');
    return (
      <>
        {textParts.map((text, i) => (
          <React.Fragment key={i}>
            {renderLines(text, i)}
            {codeBlocks[i] && <CodeBlock key={`cb-${i}`} code={codeBlocks[i].code} language={codeBlocks[i].language} />}
          </React.Fragment>
        ))}
      </>
    );
  };

  const agentName = MODE_NAMES[mode] || mode;
  const modeIcon = isCodingMode ? '⚙️' : (MODE_ICONS[mode] || '🧠');

  const formatTokens = (n: number) => n >= 1000 ? `${(n / 1000).toFixed(1)}k` : String(n);
  const formatTime = (ms: number) => ms >= 1000 ? `${(ms / 1000).toFixed(1)}s` : `${ms}ms`;

  const placeholder = isCodingMode
    ? `Ask VULCAN to read, write, or debug code... (Shift+Enter for new line)`
    : `Message ${agentName}... (Shift+Enter for new line)`;

  return (
    <div className={`chat-window ${isCodingMode ? 'coding-mode' : ''}`}>
      {workspace && (
        <div className="workspace-bar">
          <span className="workspace-bar-icon">📁</span>
          <span className="workspace-bar-path">{workspace}</span>
        </div>
      )}

      <div className="chat-messages">
        {messages.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">{modeIcon}</div>
            <div className="empty-title">{isCodingMode ? 'VULCAN · Technical' : agentName}</div>
            <div className="empty-sub">{isCodingMode ? 'Read code, run commands, deploy' : `${mode.toLowerCase()} decisions`}</div>
          </div>
        ) : (
          messages.map(msg => (
            <div key={msg.id} className={`message message-${msg.role}`}>
              <div className="message-header">
                <span className="message-sender">
                  {msg.role === 'user' ? 'You' : agentName}
                </span>
                <span className="message-time-stamp">
                  {new Date(msg.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </span>
                {msg.role === 'assistant' && msg.tokensOut && (
                  <span className="message-tokens">
                    {msg.tokensIn ? `↑${formatTokens(msg.tokensIn)} ` : ''}↓{formatTokens(msg.tokensOut)}
                    {msg.durationMs ? ` · ${formatTime(msg.durationMs)}` : ''}
                  </span>
                )}
              </div>

              {msg.attachedFile && (
                <div className="message-attachment">
                  📎 {msg.attachedFile.name}
                </div>
              )}

              <div className="message-content">
                {renderContent(msg.content)}
              </div>
            </div>
          ))
        )}
        <div ref={messagesEndRef} />
      </div>

      {loading && (
        <div className="streaming-bar">
          <span className="streaming-dot" />
          <span className="streaming-label">{agentName} is thinking</span>
          <span className="streaming-stats">
            {streamingElapsed > 0 && <span>{streamingElapsed}s</span>}
            {streamingTokensOut > 0 && <span>~{formatTokens(streamingTokensOut)} out</span>}
            {streamingTokensIn > 0 && <span>↑{formatTokens(streamingTokensIn)} in</span>}
          </span>
        </div>
      )}

      <div className="chat-input-area">
        {selectedFile && (
          <div className="file-badge">
            📎 {selectedFile.name}
            <button className="file-badge-remove" onClick={() => setSelectedFile(null)}>✕</button>
          </div>
        )}

        <div className="input-row">
          <FileUpload onFileSelect={setSelectedFile} disabled={loading} />

          <textarea
            ref={textareaRef}
            value={input}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            disabled={loading}
            rows={1}
          />

          <button
            onClick={handleSend}
            disabled={loading || !input.trim()}
            className="send-button"
          >
            {loading ? '…' : '↑'}
          </button>
        </div>

        <div className="input-hint">Enter to send · Shift+Enter for new line</div>
      </div>
    </div>
  );
}
